import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiFetch } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { getTz, fmtTs } from '../lib/globalTz';
import {
  createReportPdf, drawSectionBanner, drawTable, drawEmptyState, drawClosingFooter,
  finalizeFooters, fmtGeneratedAt, savePdf, statusCell, riskCell, ensureSpace,
  preloadPdfLogo, MARGIN_X, CONTENT_W, type TableCell, type DocCtx,
} from '../lib/pdfTemplate';

type Lookback = '1h' | '4h' | '8h' | '12h' | '24h' | '7d' | '30d' | '60d' | '90d';
type StatusTone = 'Healthy' | 'Warning' | 'Critical' | 'Unknown';

type MissionKpi = { id: string; label: string; value: string | number; status: StatusTone; helper: string; targetTab: string; targetCustomerId?: string | null };
type MissionAgent = { id: string; label: string; module: string; status: StatusTone; findings: number; lastRun: string | null; recommendedAction: string; targetTab: string; targetCustomerId?: string | null };
type MissionPipelineLink = { label: string; targetTab: string; count: number; status: StatusTone };
type MissionPipelineStep = { id: string; label: string; status: StatusTone; count: number; errors: number; healthPercent: number; errorSummary: string; helper: string; targetTab: string; links?: MissionPipelineLink[] };
type RiskTile = { customerId: string; customerName: string; riskIndex: number; riskLevel: string; reason: string; currentIncidentCount: number; criticalUnresolvedCount: number; connectorHealth: string; edrHealth: string; tiHitCount: number; slaBreachRisk: number; lastSuccessfulLogIngestion: string | null };

type MissionPayload = {
  ok: true;
  lookback: Lookback;
  generatedAt: string;
  scope: { requestedCustomerId: string | null; customerCount: number; customers: Array<{ id: string; name: string; subscription?: string; resourceGroup?: string }> };
  totals: Record<string, number>;
  kpis: MissionKpi[];
  agents: MissionAgent[];
  customerRisk: { tiles: RiskTile[]; totals: Record<string, number>; source: string };
  incidentResponse: { criticalHighIncidents: number; activeIncidents: number; customerUpdateEvents: number; topCustomers: RiskTile[] };
  huntThreatIntel: { huntFindings: number; highHuntFindings: number; huntRuns: number; failedHuntRuns: number; openBookmarks: number; recentFindings: Array<Record<string, unknown>> };
  responseOps: { playbookInventory: number; playbookPolicies: number; responseDeployments: number; openRuntimeIssues: number; criticalRuntimeIssues: number; recentIssues: Array<Record<string, unknown>>; recentPlaybooks: Array<Record<string, unknown>> };
  dataHealth: { connectorSnapshots: number; connectorCritical: number; connectorWarning: number; socDebtSnapshots: number; detectionQualitySnapshots: number; recentAuditEvents: number };
  pipeline: MissionPipelineStep[];
  recommendedActions: Array<{ title: string; detail: string; targetTab: string }>;
  diagnostics: string[];
};


type MissionBriefingKind = 'soc_briefing' | 'recommended_actions' | 'shift_handover' | 'executive_summary';
type MissionBriefing = {
  ok: true;
  kind: MissionBriefingKind;
  title: string;
  markdown: string;
  actions: Array<{ title: string; detail: string; targetTab: string }>;
  confidence: number;
  usedAi: boolean;
  model: string;
  generatedAt: string;
  diagnostics?: string[];
};

type MlStatusTone = 'Healthy' | 'Warning' | 'Critical' | 'Unknown';
type MlIncidentScore = { id: string; customerId: string; customerName: string; incidentId: string; incidentNumber: string; title: string; severity: string; status: string; owner: string; createdTimeUtc: string | null; lastModifiedTimeUtc: string | null; mlPriorityScore: number; falsePositiveLikelihood: number; confidence: number; recommendedAction: string; explanation: string[]; featureVector?: Record<string, unknown> };
type MlConnectorAnomaly = { id: string; customerId: string; customerName: string; connectorName: string; tableName: string; expectedEvents: number | null; actualEvents: number; zScore: number | null; anomalyScore: number; status: MlStatusTone; explanation: string[]; lastEventTime: string | null };
type MlEntityBaseline = { id: string; customerId: string; customerName: string; entityKey: string; entityType: string; observationCount: number; averageDailyIncidents: number; currentIncidentCount: number; recurrenceScore: number; anomalyScore: number; firstSeen: string | null; lastSeen: string | null; explanation: string[] };
type MlPayload = {
  ok: true;
  mode: 'rules' | 'hybrid_rules' | 'hybrid_python' | 'python_worker';
  lookback: Lookback;
  generatedAt: string;
  cacheHit?: boolean;
  run: { id: string; status: 'completed' | 'partial' | 'failed'; startedAt: string; completedAt: string; durationMs: number; customerCount: number; modelVersion: string };
  scope: { requestedCustomerId: string | null; customerCount: number; customers: Array<{ id: string; name: string }> };
  cards: Array<{ id: string; label: string; value: string | number; status: MlStatusTone; helper: string }>;
  incidentScores: MlIncidentScore[];
  connectorAnomalies: MlConnectorAnomaly[];
  entityBaselines: MlEntityBaseline[];
  recommendations: Array<{ priority: 'Low' | 'Medium' | 'High' | 'Critical'; title: string; details: string; targetTab: string; customerId?: string | null }>;
  feedbackSummary: { total: number; positive: number; negative: number; lastFeedbackAt: string | null };
  modelHealth: { modelVersion: string; mode: string; trainingDataWindow: string; lastRunAt: string; driftWarning: string | null; fallbackStatus: string; confidence: number };
  diagnostics: string[];
};

type Props = {
  customerId?: string;
  customerName?: string;
  onNavigate?: (tabId: string, customerId?: string | null) => void;
};

const LOOKBACKS: Lookback[] = ['1h', '4h', '8h', '12h', '24h', '7d', '30d', '60d', '90d'];

function statusClass(status: StatusTone | string) {
  const s = String(status).toLowerCase();
  if (s.includes('critical')) return 'border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-200';
  if (s.includes('warning') || s.includes('high')) return 'border-orange-500/40 bg-orange-500/10 text-orange-700 dark:text-orange-200';
  if (s.includes('unknown') || s.includes('medium')) return 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-200';
  return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200';
}

function statusDotClass(status: StatusTone | string) {
  const s = String(status).toLowerCase();
  if (s.includes('critical')) return 'bg-red-400 shadow-[0_0_18px_rgba(248,113,113,0.75)]';
  if (s.includes('warning') || s.includes('high')) return 'bg-orange-400 shadow-[0_0_18px_rgba(251,146,60,0.75)]';
  if (s.includes('unknown') || s.includes('medium')) return 'bg-amber-300 shadow-[0_0_18px_rgba(252,211,77,0.75)]';
  return 'bg-emerald-400 shadow-[0_0_18px_rgba(52,211,153,0.75)]';
}

function healthBarClass(status: StatusTone | string) {
  const s = String(status).toLowerCase();
  if (s.includes('critical')) return 'bg-red-400';
  if (s.includes('warning') || s.includes('high')) return 'bg-orange-400';
  if (s.includes('unknown') || s.includes('medium')) return 'bg-amber-300';
  return 'bg-emerald-400';
}

function statusTextClass(status: StatusTone | string) {
  const s = String(status).toLowerCase();
  if (s.includes('critical')) return 'text-red-200';
  if (s.includes('warning') || s.includes('high')) return 'text-orange-200';
  if (s.includes('unknown') || s.includes('medium')) return 'text-amber-200';
  return 'text-emerald-200';
}

function riskClass(level: string, score?: number) {
  const s = level.toLowerCase();
  if (s.includes('critical') || (score ?? 0) >= 85) return 'border-red-500/50 bg-red-500/10';
  if (s.includes('high') || (score ?? 0) >= 65) return 'border-orange-500/50 bg-orange-500/10';
  if (s.includes('medium') || (score ?? 0) >= 35) return 'border-amber-500/50 bg-amber-500/10';
  return 'border-emerald-500/40 bg-emerald-500/10';
}

function text(value: unknown, fallback = '-') {
  const s = String(value ?? '').trim();
  return s || fallback;
}

function fmtDate(value: unknown) {
  if (!value) return 'Not available';
  const d = new Date(String(value));
  // Client-selected timezone (globalTz), not the analyst's own browser locale.
  return Number.isNaN(d.getTime()) ? 'Not available' : fmtTs(d);
}

function downloadText(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function exportMissionPdf(payload: MissionPayload, opts: { customerLabel: string; filenameBase: string }) {
  await preloadPdfLogo();
  const ctx = createReportPdf({
    title: 'Mission Control',
    customerName: opts.customerLabel,
    periodLabel: `Lookback ${payload.lookback} · ${payload.scope.customerCount} customer(s)`,
    generatedLabel: fmtGeneratedAt(payload.generatedAt, getTz()),
  });

  drawSectionBanner(ctx, 'Command KPIs');
  if (payload.kpis.length === 0) {
    drawEmptyState(ctx, 'No KPI data available.');
  } else {
    const rows: TableCell[][] = payload.kpis.map((k) => [
      k.label,
      { content: String(k.value), styles: { halign: 'center', fontStyle: 'bold' } },
      statusCell(k.status),
      k.helper,
    ]);
    drawTable(ctx, {
      head: ['KPI', 'Value', 'Status', 'Detail'],
      body: rows,
      columnStyles: { 0: { cellWidth: 45 }, 1: { cellWidth: 22, halign: 'center' }, 2: { cellWidth: 26, halign: 'center' }, 3: { cellWidth: 'auto' } },
      minRowHeight: 8,
    });
  }

  drawSectionBanner(ctx, 'AI SOC Agents');
  if (payload.agents.length === 0) {
    drawEmptyState(ctx, 'No agent data available.');
  } else {
    const rows: TableCell[][] = payload.agents.map((a) => [
      a.label,
      statusCell(a.status),
      { content: String(a.findings), styles: { halign: 'center' } },
      a.recommendedAction,
    ]);
    drawTable(ctx, {
      head: ['Agent', 'Status', 'Signals', 'Recommended Action'],
      body: rows,
      columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 24, halign: 'center' }, 2: { cellWidth: 20, halign: 'center' }, 3: { cellWidth: 'auto' } },
      minRowHeight: 8,
    });
  }

  drawSectionBanner(ctx, 'Customer Risk Wall');
  const tiles = payload.customerRisk.tiles.slice(0, 20);
  if (tiles.length === 0) {
    drawEmptyState(ctx, 'No customer risk data available.');
  } else {
    const rows: TableCell[][] = tiles.map((t) => [
      t.customerName,
      { content: String(t.riskIndex), styles: { halign: 'center', fontStyle: 'bold' } },
      riskCell(t.riskLevel),
      t.reason,
    ]);
    drawTable(ctx, {
      head: ['Customer', 'Risk Index', 'Risk Level', 'Reason'],
      body: rows,
      columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 22, halign: 'center' }, 2: { cellWidth: 26, halign: 'center' }, 3: { cellWidth: 'auto' } },
      minRowHeight: 8,
    });
  }

  drawSectionBanner(ctx, 'Workflow Pipeline');
  if (payload.pipeline.length === 0) {
    drawEmptyState(ctx, 'No pipeline data available.');
  } else {
    const rows: TableCell[][] = payload.pipeline.map((s) => [
      s.label,
      statusCell(s.status),
      { content: String(s.count), styles: { halign: 'center' } },
      s.helper,
    ]);
    drawTable(ctx, {
      head: ['Stage', 'Status', 'Count', 'Detail'],
      body: rows,
      columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 24, halign: 'center' }, 2: { cellWidth: 18, halign: 'center' }, 3: { cellWidth: 'auto' } },
      minRowHeight: 8,
    });
  }

  if (payload.recommendedActions.length) {
    drawSectionBanner(ctx, 'Recommended Actions');
    const rows: TableCell[][] = payload.recommendedActions.map((a) => [a.title, a.detail]);
    drawTable(ctx, {
      head: ['Action', 'Detail'],
      body: rows,
      columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 'auto' } },
      minRowHeight: 8,
    });
  }

  drawClosingFooter(ctx);
  finalizeFooters(ctx);
  return savePdf(ctx, opts.filenameBase);
}


function markdownToPlain(markdown: string) {
  return String(markdown ?? '')
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/\*\*/g, '')
    .replace(/`/g, '')
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
    .trim();
}

async function copyText(value: string) {
  await navigator.clipboard.writeText(value);
}

/** Long-form narrative body text (not tabular) — paragraphs with page-break
 *  handling, drawn between the branded header and footer. */
function addParagraphs(ctx: DocCtx, text: string, size = 9.5) {
  const { pdf } = ctx;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(size);
  for (const block of text.split(/\n{2,}/)) {
    const lines = pdf.splitTextToSize(block || ' ', CONTENT_W);
    for (const line of lines) {
      ensureSpace(ctx, 6);
      pdf.text(String(line), MARGIN_X, ctx.y);
      ctx.y += 5;
    }
    ctx.y += 3;
  }
}

async function exportBriefingPdf(briefing: MissionBriefing, opts: { customerLabel: string; filenameBase: string }) {
  await preloadPdfLogo();
  const ctx = createReportPdf({
    title: briefing.title || 'Mission Control Briefing',
    customerName: opts.customerLabel,
    periodLabel: briefing.usedAi ? `AI model ${briefing.model}` : 'Deterministic fallback',
    generatedLabel: fmtGeneratedAt(briefing.generatedAt, getTz()),
  });

  drawSectionBanner(ctx, `Briefing · Confidence ${briefing.confidence}%`);
  addParagraphs(ctx, markdownToPlain(briefing.markdown));

  if (briefing.actions?.length) {
    ctx.y += 3;
    drawSectionBanner(ctx, 'Recommended Actions');
    const rows: TableCell[][] = briefing.actions.map((a) => [a.title, a.detail]);
    drawTable(ctx, {
      head: ['Action', 'Detail'],
      body: rows,
      columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 'auto' } },
      minRowHeight: 8,
    });
  }

  drawClosingFooter(ctx);
  finalizeFooters(ctx);
  return savePdf(ctx, opts.filenameBase);
}

function BriefingPanel({ briefing, onOpen, onCopy, onExport }: { briefing: MissionBriefing; onOpen: (tabId: string) => void; onCopy: () => void; onExport: () => void }) {
  return <div className="rounded-3xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 via-blue-500/10 to-indigo-500/10 p-5 shadow-sm">
    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
      <div>
        <div className="text-xs font-black uppercase tracking-[0.25em] text-cyan-600 dark:text-cyan-300">Mission Intelligence</div>
        <h3 className="mt-1 text-xl font-black">{briefing.title}</h3>
        <div className="mt-1 text-xs text-muted-foreground">{briefing.usedAi ? `AI-generated using ${briefing.model}` : `Deterministic fallback${briefing.diagnostics?.length ? ` · ${briefing.diagnostics[0]}` : ''}`} · Confidence {briefing.confidence}% · {fmtDate(briefing.generatedAt)}</div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={onCopy} className="rounded-xl border px-3 py-2 text-xs font-bold hover:bg-muted">Copy briefing</button>
        <button type="button" onClick={onExport} className="rounded-xl border px-3 py-2 text-xs font-bold hover:bg-muted">Export briefing PDF</button>
      </div>
    </div>
    <div className="mt-4 max-h-[420px] overflow-auto whitespace-pre-wrap rounded-2xl border bg-background/80 p-4 text-sm leading-6">{briefing.markdown}</div>
    {briefing.actions?.length ? <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
      {briefing.actions.slice(0, 8).map((action, idx) => <button key={`${action.title}-${idx}`} type="button" onClick={() => onOpen(action.targetTab)} className="rounded-2xl border bg-background/70 p-3 text-left text-sm hover:bg-muted/60">
        <div className="font-black">{idx + 1}. {action.title}</div>
        <div className="mt-1 text-xs text-muted-foreground">{action.detail}</div>
      </button>)}
    </div> : null}
  </div>;
}

function AgentCoreVisual({ agents, pipeline, totals, onOpen }: { agents: MissionAgent[]; pipeline: MissionPipelineStep[]; totals: Record<string, number>; onOpen: (tabId: string, customerId?: string | null) => void }) {
  const liveSignals = Object.values(totals ?? {}).reduce((acc, value) => acc + (Number.isFinite(Number(value)) ? Number(value) : 0), 0);
  const criticalAgents = agents.filter((a) => String(a.status).toLowerCase().includes('critical')).length;
  const warningAgents = agents.filter((a) => String(a.status).toLowerCase().includes('warning')).length;
  const healthyAgents = agents.length - criticalAgents - warningAgents;
  const confidence = Math.max(55, Math.min(99, Math.round(92 - criticalAgents * 8 - warningAgents * 3 + healthyAgents)));
  const pipelineHealth = pipeline.length ? Math.round(pipeline.reduce((acc, step) => acc + Number(step.healthPercent ?? 0), 0) / pipeline.length) : 0;
  return <Section title="AI SOC Core Engine" subtitle="Six live agents represent the current operational domains: incidents, hunting, response, customer risk, data health and evidence/governance.">
    <div className="relative overflow-hidden rounded-[2rem] border border-cyan-300/20 bg-[radial-gradient(circle_at_50%_35%,rgba(34,211,238,0.26),transparent_34%),linear-gradient(135deg,#020617,#0f172a_45%,#172554)] p-5 text-white shadow-[0_24px_90px_rgba(2,6,23,0.55)]">
      <div className="pointer-events-none absolute inset-0 opacity-40 [background-image:linear-gradient(rgba(148,163,184,0.10)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.10)_1px,transparent_1px)] [background-size:32px_32px]" />
      <div className="relative grid grid-cols-1 gap-5 xl:grid-cols-[1fr_390px_1fr]">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-1">
          {agents.slice(0, Math.ceil(agents.length / 2)).map((agent) => <button key={agent.id} type="button" onClick={() => onOpen(agent.targetTab, agent.targetCustomerId)} className="group rounded-3xl border border-white/15 bg-white/[0.06] p-4 text-left backdrop-blur transition hover:-translate-y-0.5 hover:border-cyan-300/50 hover:bg-white/10">
            <div className="flex items-center justify-between gap-3"><span className="font-black">{agent.label}</span><span className={`h-3 w-3 rounded-full ${statusDotClass(agent.status)}`} /></div>
            <div className="mt-1 text-xs text-blue-100/70">{agent.module}</div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-2xl border border-white/10 bg-black/20 p-2"><div className="text-lg font-black">{agent.findings}</div><div className="text-blue-100/60">signals</div></div>
              <div className="rounded-2xl border border-white/10 bg-black/20 p-2"><div className={`font-black ${statusTextClass(agent.status)}`}>{agent.status}</div><div className="text-blue-100/60">status</div></div>
            </div>
          </button>)}
        </div>
        <div className="flex min-h-[390px] flex-col items-center justify-center">
          <div className="relative flex h-80 w-80 items-center justify-center rounded-full border border-cyan-200/40 bg-cyan-400/10 shadow-[0_0_90px_rgba(34,211,238,0.28)]">
            <div className="absolute h-72 w-72 animate-pulse rounded-full border border-blue-300/20" />
            <div className="absolute h-60 w-60 rounded-full border border-cyan-300/25" />
            <div className="absolute h-44 w-44 rounded-full border border-indigo-300/30" />
            {agents.slice(0, 6).map((agent, idx) => {
              const positions = ['left-1/2 top-2 -translate-x-1/2', 'right-3 top-16', 'right-5 bottom-16', 'left-1/2 bottom-2 -translate-x-1/2', 'left-5 bottom-16', 'left-3 top-16'];
              return <button key={`orbit-${agent.id}`} type="button" onClick={() => onOpen(agent.targetTab, agent.targetCustomerId)} title={`${agent.label}: ${agent.status}`} className={`absolute ${positions[idx] ?? ''} flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-slate-950/85 text-[10px] font-black shadow-xl hover:border-cyan-300`}>
                <span className={`absolute -right-1 -top-1 h-3 w-3 rounded-full ${statusDotClass(agent.status)}`} />
                {idx + 1}
              </button>;
            })}
            <div className="z-10 flex h-40 w-40 flex-col items-center justify-center rounded-full border border-white/20 bg-slate-950/95 text-center shadow-2xl">
              <div className="text-[10px] font-black uppercase tracking-[0.24em] text-cyan-200">KloudSecure</div>
              <div className="mt-1 text-2xl font-black">AI SOC</div>
              <div className="text-2xl font-black">Core</div>
              <div className="mt-2 text-[10px] text-blue-100/70">Sense · Reason · Respond</div>
            </div>
          </div>
          <div className="mt-5 grid w-full grid-cols-3 gap-2 text-center text-xs">
            <div className="rounded-2xl border border-white/15 bg-white/5 p-3"><div className="text-2xl font-black">{confidence}%</div><div className="text-blue-100/70">AI confidence</div></div>
            <div className="rounded-2xl border border-white/15 bg-white/5 p-3"><div className="text-2xl font-black">{agents.length}</div><div className="text-blue-100/70">live agents</div></div>
            <div className="rounded-2xl border border-white/15 bg-white/5 p-3"><div className="text-2xl font-black">{pipelineHealth}%</div><div className="text-blue-100/70">pipeline health</div></div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-1">
          {agents.slice(Math.ceil(agents.length / 2)).map((agent) => <button key={agent.id} type="button" onClick={() => onOpen(agent.targetTab, agent.targetCustomerId)} className="group rounded-3xl border border-white/15 bg-white/[0.06] p-4 text-left backdrop-blur transition hover:-translate-y-0.5 hover:border-cyan-300/50 hover:bg-white/10">
            <div className="flex items-center justify-between gap-3"><span className="font-black">{agent.label}</span><span className={`rounded-full border border-white/15 px-2 py-0.5 text-[10px] font-black ${statusTextClass(agent.status)}`}>{agent.status}</span></div>
            <div className="mt-2 text-xs text-blue-100/75">{agent.recommendedAction}</div>
            <div className="mt-3 text-xs text-blue-100/60">Click to open {agent.module}</div>
          </button>)}
        </div>
      </div>
      <div className="relative mt-5 rounded-3xl border border-white/10 bg-black/20 p-3 text-xs text-blue-100/75">
        The six agents are intentionally grouped by SOC function rather than by vendor: Incident Analyst, Threat Hunting, ResponseOps, RiskOps, Data Health and Evidence/Governance. Their status is recalculated from the same Mission Control backend signals shown in the cards below.
      </div>
    </div>
  </Section>;
}

function MetricCard({ kpi, onOpen }: { kpi: MissionKpi; onOpen: () => void }) {
  return <button type="button" onClick={onOpen} className="group relative overflow-hidden rounded-3xl border border-cyan-500/10 bg-gradient-to-br from-card via-card to-muted/50 p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-cyan-400/40 hover:shadow-xl">
    <div className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-cyan-400/10 blur-2xl transition group-hover:bg-cyan-400/20" />
    <div className="relative flex items-start justify-between gap-3">
      <div className="text-xs font-black uppercase tracking-wide text-muted-foreground">{kpi.label}</div>
      <span className={`h-2.5 w-2.5 rounded-full ${statusDotClass(kpi.status)}`} />
    </div>
    <div className="relative mt-3 text-4xl font-black tracking-tight">{kpi.value}</div>
    <div className="relative mt-3 text-xs leading-5 text-muted-foreground">{kpi.helper}</div>
  </button>;
}

function scoreBandClass(score: number) {
  if (score >= 85) return 'border-red-500/50 bg-red-500/10 text-red-700 dark:text-red-200';
  if (score >= 70) return 'border-orange-500/50 bg-orange-500/10 text-orange-700 dark:text-orange-200';
  if (score >= 45) return 'border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-200';
  return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200';
}

function MlFeedbackButtons({ onFeedback }: { onFeedback: (feedback: string) => void }) {
  const buttons = [
    ['useful', 'Useful'],
    ['not_useful', 'Not useful'],
    ['true_positive', 'TP'],
    ['false_positive', 'FP'],
  ];
  return <div className="flex flex-wrap gap-1.5">
    {buttons.map(([id, label]) => <button key={id} type="button" onClick={(event) => { event.stopPropagation(); onFeedback(id); }} className="rounded-full border px-2 py-1 text-[10px] font-bold text-muted-foreground transition hover:border-primary/60 hover:text-foreground">{label}</button>)}
  </div>;
}

function MlInsightsPanel({ payload, loading, error, canRefresh, canExport, canFeedback, canModelHealth, onRefresh, onExport, onFeedback, onOpen }: {
  payload: MlPayload | null;
  loading: boolean;
  error: string | null;
  canRefresh: boolean;
  canExport: boolean;
  canFeedback: boolean;
  canModelHealth: boolean;
  onRefresh: (force?: boolean) => void;
  onExport: () => void;
  onFeedback: (input: { customerId?: string | null; insightType: string; targetId: string; targetLabel: string; feedback: string; payload?: unknown }) => void;
  onOpen: (tabId: string, customerId?: string | null) => void;
}) {
  const topIncidents = payload?.incidentScores?.slice(0, 8) ?? [];
  const connectors = payload?.connectorAnomalies?.filter((c) => c.anomalyScore >= 50).slice(0, 6) ?? [];
  const entities = payload?.entityBaselines?.slice(0, 6) ?? [];
  return <Section title="ML Insights" subtitle="Phase 1 lightweight, explainable ML: PostgreSQL + backend scoring using z-score style baselines, rolling patterns, weighted scoring, entity recurrence and analyst feedback. No Python worker required.">
    <div className="overflow-hidden rounded-[2rem] border border-violet-500/30 bg-gradient-to-br from-slate-950 via-violet-950 to-slate-900 p-5 text-white shadow-[0_24px_90px_rgba(76,29,149,0.28)]">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="text-xs font-black uppercase tracking-[0.28em] text-violet-200/80">Machine Learning Capability</div>
          <h3 className="mt-1 text-2xl font-black">Mission Control → ML Insights</h3>
          <p className="mt-1 max-w-4xl text-sm text-violet-100/75">Incident priority scoring, false-positive likelihood, entity recurrence baselines, connector anomaly detection, feedback capture and model health in one explainable SOC panel.</p>
          {payload && <div className="mt-2 text-xs text-violet-100/65">Run {payload.run.status} · {payload.run.modelVersion} · {payload.run.durationMs} ms · {payload.cacheHit ? 'cached snapshot' : 'fresh scoring'} · {payload.scope.customerCount} customer(s)</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          {canRefresh && <button type="button" onClick={() => onRefresh(false)} disabled={loading} className="rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-xs font-black text-white hover:bg-white/15 disabled:opacity-60">{loading ? 'Scoring…' : 'Refresh'}</button>}
          {canRefresh && <button type="button" onClick={() => onRefresh(true)} disabled={loading} className="rounded-xl bg-violet-300 px-3 py-2 text-xs font-black text-slate-950 hover:bg-violet-200 disabled:opacity-60">Re-score</button>}
          {canExport && payload && <button type="button" onClick={onExport} className="rounded-xl border border-white/20 px-3 py-2 text-xs font-black text-white hover:bg-white/10">Export CSV</button>}
        </div>
      </div>

      {error && <div className="mt-4 rounded-2xl border border-red-300/30 bg-red-500/10 p-3 text-sm text-red-100">{error}</div>}
      {!payload && !loading && !error && <div className="mt-4 rounded-2xl border border-white/15 bg-white/5 p-4 text-sm text-violet-100/75">ML Insights will appear here once the scoring endpoint returns data for the selected scope.</div>}

      {payload && <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
        {payload.cards.map((card) => <button key={card.id} type="button" onClick={() => card.id === 'connector_anomaly' ? onOpen('connector_health') : card.id === 'entity_recurrence' ? onOpen('entity_360') : onOpen('incidents')} className="rounded-2xl border border-white/15 bg-white/[0.07] p-4 text-left transition hover:-translate-y-0.5 hover:border-violet-200/60 hover:bg-white/10">
          <div className="flex items-center justify-between gap-2"><span className="text-[10px] font-black uppercase tracking-wide text-violet-100/60">{card.label}</span><span className={`h-2.5 w-2.5 rounded-full ${statusDotClass(card.status)}`} /></div>
          <div className="mt-2 text-2xl font-black">{card.value}</div>
          <div className="mt-1 text-[11px] leading-4 text-violet-100/65">{card.helper}</div>
        </button>)}
      </div>}

      {payload && <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-[1.35fr_0.9fr]">
        <div className="rounded-3xl border border-white/15 bg-white/[0.06] p-4">
          <div className="flex items-center justify-between gap-3"><div><div className="font-black">ML Incident Prioritization</div><div className="text-xs text-violet-100/60">Explainable priority score + false-positive likelihood. Recommendations only; no auto-close.</div></div><span className="rounded-full border border-white/15 px-2 py-1 text-[10px] font-black uppercase text-violet-100/70">Top {topIncidents.length}</span></div>
          <div className="mt-3 space-y-2">
            {topIncidents.length === 0 ? <div className="rounded-2xl border border-white/10 bg-black/15 p-4 text-sm text-violet-100/70">No active incidents were scored. Check diagnostics or run Re-score for a selected customer.</div> : topIncidents.map((incident) => <button key={incident.id} type="button" onClick={() => onOpen('incidents', incident.customerId)} className="w-full rounded-2xl border border-white/10 bg-black/15 p-3 text-left transition hover:border-violet-200/50 hover:bg-white/10">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0"><div className="truncate font-black">{incident.title}</div><div className="mt-1 text-[11px] text-violet-100/60">{incident.customerName} · {incident.severity} · FP {incident.falsePositiveLikelihood}% · confidence {incident.confidence}%</div></div>
                <div className="flex shrink-0 flex-col items-start gap-2 lg:items-end"><span className={`rounded-full border px-2 py-1 text-xs font-black ${scoreBandClass(incident.mlPriorityScore)}`}>{incident.mlPriorityScore}/100</span>{canFeedback && <MlFeedbackButtons onFeedback={(feedback) => onFeedback({ customerId: incident.customerId, insightType: 'incident_score', targetId: incident.incidentId, targetLabel: incident.title, feedback, payload: { score: incident.mlPriorityScore, fp: incident.falsePositiveLikelihood } })} />}</div>
              </div>
              <div className="mt-2 text-xs text-violet-100/70">{incident.recommendedAction}</div>
              <details className="mt-2 text-[11px] text-violet-100/60"><summary className="cursor-pointer font-bold">Why this score?</summary><ul className="mt-1 list-disc space-y-1 pl-4">{incident.explanation.slice(0, 5).map((e, idx) => <li key={idx}>{e}</li>)}</ul></details>
            </button>)}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-3xl border border-white/15 bg-white/[0.06] p-4">
            <div className="font-black">Connector Anomalies</div>
            <div className="mt-1 text-xs text-violet-100/60">Freshness, volume and delay signals from Connector Health cache.</div>
            <div className="mt-3 space-y-2">
              {connectors.length === 0 ? <div className="rounded-2xl border border-white/10 bg-black/15 p-3 text-xs text-violet-100/70">No material connector anomaly above the display threshold.</div> : connectors.map((row) => <button key={row.id} type="button" onClick={() => onOpen('connector_health', row.customerId)} className="w-full rounded-2xl border border-white/10 bg-black/15 p-3 text-left hover:border-violet-200/50 hover:bg-white/10">
                <div className="flex items-center justify-between gap-2"><span className="truncate text-sm font-black">{row.tableName}</span><span className={`rounded-full border px-2 py-0.5 text-[10px] font-black ${statusClass(row.status)}`}>{row.anomalyScore}</span></div>
                <div className="mt-1 text-[11px] text-violet-100/60">{row.customerName} · {row.connectorName} · actual {row.actualEvents}{row.expectedEvents ? ` · expected ${row.expectedEvents}` : ''}</div>
              </button>)}
            </div>
          </div>

          <div className="rounded-3xl border border-white/15 bg-white/[0.06] p-4">
            <div className="font-black">Entity Recurrence Baselines</div>
            <div className="mt-1 text-xs text-violet-100/60">High-recurrence users/devices/IPs/domains from incident history.</div>
            <div className="mt-3 space-y-2">
              {entities.length === 0 ? <div className="rounded-2xl border border-white/10 bg-black/15 p-3 text-xs text-violet-100/70">No recurring entity baseline was strong enough to display.</div> : entities.map((entity) => <button key={entity.id} type="button" onClick={() => onOpen('entity_360', entity.customerId)} className="w-full rounded-2xl border border-white/10 bg-black/15 p-3 text-left hover:border-violet-200/50 hover:bg-white/10">
                <div className="flex items-center justify-between gap-2"><span className="truncate text-sm font-black">{entity.entityKey}</span><span className="rounded-full border border-white/15 px-2 py-0.5 text-[10px] font-black text-violet-100/80">{entity.anomalyScore}/100</span></div>
                <div className="mt-1 text-[11px] text-violet-100/60">{entity.customerName} · {entity.entityType} · {entity.observationCount} observations · {entity.currentIncidentCount} active</div>
              </button>)}
            </div>
          </div>
        </div>
      </div>}

      {payload && <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="rounded-3xl border border-white/15 bg-white/[0.06] p-4">
          <div className="font-black">ML Recommendations</div>
          <div className="mt-3 space-y-2">{payload.recommendations.slice(0, 6).map((rec, idx) => <button key={`${rec.title}-${idx}`} type="button" onClick={() => onOpen(rec.targetTab, rec.customerId ?? undefined)} className="w-full rounded-2xl border border-white/10 bg-black/15 p-3 text-left hover:border-violet-200/50 hover:bg-white/10"><div className="flex items-start justify-between gap-2"><div><div className="font-bold">{rec.title}</div><div className="mt-1 text-xs text-violet-100/65">{rec.details}</div></div><span className={`rounded-full border px-2 py-0.5 text-[10px] font-black ${statusClass(rec.priority)}`}>{rec.priority}</span></div></button>)}</div>
        </div>
        {canModelHealth && <div className="rounded-3xl border border-white/15 bg-white/[0.06] p-4">
          <div className="font-black">Model Health + Governance</div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            <div className="rounded-2xl border border-white/10 bg-black/15 p-3"><div className="text-violet-100/60">Version</div><div className="mt-1 font-black">{payload.modelHealth.modelVersion}</div></div>
            <div className="rounded-2xl border border-white/10 bg-black/15 p-3"><div className="text-violet-100/60">Confidence</div><div className="mt-1 font-black">{payload.modelHealth.confidence}%</div></div>
            <div className="rounded-2xl border border-white/10 bg-black/15 p-3"><div className="text-violet-100/60">Window</div><div className="mt-1 font-black">{payload.modelHealth.trainingDataWindow}</div></div>
            <div className="rounded-2xl border border-white/10 bg-black/15 p-3"><div className="text-violet-100/60">Feedback</div><div className="mt-1 font-black">{payload.feedbackSummary.total} events</div></div>
          </div>
          <div className="mt-3 rounded-2xl border border-white/10 bg-black/15 p-3 text-xs text-violet-100/70">{payload.modelHealth.fallbackStatus}{payload.modelHealth.driftWarning ? ` · ${payload.modelHealth.driftWarning}` : ''}</div>
          {payload.diagnostics?.length ? <details className="mt-3 text-xs text-violet-100/65"><summary className="cursor-pointer font-bold">Show ML diagnostics</summary><pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded-2xl bg-black/25 p-3">{payload.diagnostics.join('\n')}</pre></details> : null}
        </div>}
      </div>}
    </div>
  </Section>;
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return <div className="rounded-[1.75rem] border border-border/70 bg-card/95 p-5 shadow-sm backdrop-blur">
    <div className="mb-4 flex items-start justify-between gap-4">
      <div>
        <h3 className="text-lg font-black">{title}</h3>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>;
}

function HeroStat({ label, value, helper, status }: { label: string; value: string | number; helper: string; status: StatusTone | string }) {
  return <div className="rounded-2xl border border-white/15 bg-white/[0.07] p-3 backdrop-blur">
    <div className="flex items-center justify-between gap-2"><span className="text-[10px] font-black uppercase tracking-wide text-blue-100/60">{label}</span><span className={`h-2.5 w-2.5 rounded-full ${statusDotClass(status)}`} /></div>
    <div className="mt-1 text-2xl font-black text-white">{value}</div>
    <div className="text-[11px] text-blue-100/65">{helper}</div>
  </div>;
}

function WorkflowPipelinePanel({ pipeline, onOpen }: { pipeline: MissionPipelineStep[]; onOpen: (tabId: string) => void }) {
  const avgHealth = pipeline.length ? Math.round(pipeline.reduce((acc, step) => acc + Number(step.healthPercent ?? 0), 0) / pipeline.length) : 0;
  const totalErrors = pipeline.reduce((acc, step) => acc + Number(step.errors ?? 0), 0);
  return <Section title="SOC Workflow Pipeline" subtitle="Phase 5 pipeline: every stage shows live health, counts, errors and drilldown links into the owning module.">
    <div className="relative overflow-hidden rounded-[2rem] border border-slate-700/60 bg-slate-950 p-5 text-white shadow-[0_24px_90px_rgba(2,6,23,0.45)]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.22),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(34,211,238,0.16),transparent_35%)]" />
      <div className="relative mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="text-xs font-black uppercase tracking-[0.28em] text-cyan-200/80">Operational Flow</div>
          <div className="mt-1 text-2xl font-black">Alert Ingestion → Investigation → Correlation → Reasoning → Response → Reporting</div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-center text-xs sm:grid-cols-3">
          <div className="rounded-2xl border border-white/15 bg-white/5 p-3"><div className="text-2xl font-black">{avgHealth}%</div><div className="text-blue-100/65">avg health</div></div>
          <div className="rounded-2xl border border-white/15 bg-white/5 p-3"><div className="text-2xl font-black">{totalErrors}</div><div className="text-blue-100/65">open issues</div></div>
          <div className="rounded-2xl border border-white/15 bg-white/5 p-3"><div className="text-2xl font-black">{pipeline.length}</div><div className="text-blue-100/65">stages</div></div>
        </div>
      </div>
      <div className="relative grid grid-cols-1 gap-4 lg:grid-cols-6">
        {pipeline.map((step, idx) => <div key={step.id} className="relative">
          {idx < pipeline.length - 1 && <div className="pointer-events-none absolute left-[calc(100%-0.5rem)] top-12 z-0 hidden h-px w-8 bg-cyan-300/35 lg:block" />}
          <button type="button" onClick={() => onOpen(step.targetTab)} className="group relative z-10 flex h-full w-full flex-col rounded-3xl border border-white/15 bg-white/[0.06] p-4 text-left transition hover:-translate-y-1 hover:border-cyan-300/60 hover:bg-white/10">
            <div className="flex items-center justify-between gap-2">
              <span className="rounded-full border border-white/15 bg-black/25 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-blue-100/65">Stage {idx + 1}</span>
              <span className={`h-3 w-3 rounded-full ${statusDotClass(step.status)}`} />
            </div>
            <div className="mt-3 text-lg font-black">{step.label}</div>
            <div className="mt-1 text-xs text-blue-100/65">{step.helper}</div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-2xl border border-white/10 bg-black/20 p-2"><div className="text-2xl font-black">{step.count}</div><div className="text-blue-100/60">signals</div></div>
              <div className="rounded-2xl border border-white/10 bg-black/20 p-2"><div className="text-2xl font-black">{step.errors}</div><div className="text-blue-100/60">issues</div></div>
            </div>
            <div className="mt-4">
              <div className="mb-1 flex items-center justify-between text-[10px] font-black uppercase tracking-wide text-blue-100/60"><span>Health</span><span>{step.healthPercent ?? 0}%</span></div>
              <div className="h-2 overflow-hidden rounded-full bg-white/10"><div className={`h-full rounded-full ${healthBarClass(step.status)}`} style={{ width: `${Math.max(4, Math.min(100, Number(step.healthPercent ?? 0)))}%` }} /></div>
            </div>
            <div className="mt-3 min-h-[48px] text-[11px] leading-5 text-blue-100/70">{step.errorSummary}</div>
            {step.links?.length ? <div className="mt-auto flex flex-wrap gap-1.5 pt-3">
              {step.links.slice(0, 3).map((link) => <span key={`${step.id}-${link.label}`} className="rounded-full border border-white/10 bg-black/20 px-2 py-1 text-[10px] text-blue-100/70">{link.label}: {link.count}</span>)}
            </div> : null}
          </button>
        </div>)}
      </div>
    </div>
  </Section>;
}

export function MissionControlTab({ customerId, customerName, onNavigate }: Props) {
  const { canCapability } = useAuth();
  const [lookback, setLookback] = useState<Lookback>('24h');
  const [scopeMode, setScopeMode] = useState<'all' | 'selected'>('all');
  const [payload, setPayload] = useState<MissionPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [briefing, setBriefing] = useState<MissionBriefing | null>(null);
  const [briefingLoading, setBriefingLoading] = useState<MissionBriefingKind | null>(null);
  const [mlPayload, setMlPayload] = useState<MlPayload | null>(null);
  const [mlLoading, setMlLoading] = useState(false);
  const [mlError, setMlError] = useState<string | null>(null);
  const [mlExporting, setMlExporting] = useState(false);

  const canRead = canCapability('mission_control', 'read');
  const canRefresh = canCapability('mission_control', 'refresh') || canRead;
  const canExport = canCapability('mission_control', 'export');
  const canDrilldown = canCapability('mission_control', 'open_drilldowns') || canRead;
  const canAiBriefing = canCapability('mission_control', 'ai_briefing');
  const canRecommendedActions = canCapability('mission_control', 'recommended_actions') || canAiBriefing;
  const canShiftHandover = canCapability('mission_control', 'shift_handover');
  const canExecutiveSummary = canCapability('mission_control', 'executive_summary');
  const canAgentVisual = canCapability('mission_control', 'view_agent_visual') || canCapability('mission_control', 'view_agent_status') || canRead;
  const canWorkflowPipeline = canCapability('mission_control', 'view_workflow_pipeline') || canRead;
  const canMlRead = canCapability('ml_insights', 'read');
  const canMlRefresh = canCapability('ml_insights', 'refresh') || canMlRead;
  const canMlExport = canCapability('ml_insights', 'export');
  const canMlFeedback = canCapability('ml_insights', 'submit_feedback');
  const canMlModelHealth = canCapability('ml_insights', 'view_model_health') || canMlRead;

  const selectedScopeCustomerId = scopeMode === 'selected' ? customerId : undefined;

  async function load() {
    if (!canRead) return;
    setLoading(true);
    setError(null);
    try {
      const customerPart = selectedScopeCustomerId ? `&customerId=${encodeURIComponent(selectedScopeCustomerId)}` : '';
      const data = await apiFetch<MissionPayload>(`/mission-control/summary?lookback=${encodeURIComponent(lookback)}${customerPart}`);
      setPayload(data);
      toast({ title: 'Mission Control loaded', description: `${data.scope.customerCount} customer(s) · ${data.lookback}` });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [lookback, scopeMode, customerId, canRead]);

  async function loadMl(force = false) {
    if (!canMlRead) return;
    setMlLoading(true);
    setMlError(null);
    try {
      const customerPart = selectedScopeCustomerId ? `&customerId=${encodeURIComponent(selectedScopeCustomerId)}` : '';
      const refreshPart = force ? '&refresh=1' : '';
      const data = await apiFetch<MlPayload>(`/ml-insights/summary?lookback=${encodeURIComponent(lookback)}${customerPart}${refreshPart}`);
      setMlPayload(data);
      if (force) toast({ title: 'ML Insights re-scored', description: `${data.incidentScores.length} incidents · ${data.connectorAnomalies.length} connector signals` });
    } catch (err) {
      setMlError(err instanceof Error ? err.message : String(err));
    } finally {
      setMlLoading(false);
    }
  }

  // ML Insights moved to a standalone top-level module. Mission Control keeps
  // the navigation handoff only, avoiding duplicate scoring calls from this tab.

  async function exportMlCsv() {
    if (!mlPayload) return;
    setMlExporting(true);
    setMlError(null);
    try {
      const result = await apiFetch<{ ok: boolean; filename: string; csv: string }>('/ml-insights/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payload: mlPayload }),
      });
      downloadText(result.filename || `kloudsecure-ml-insights-${lookback}.csv`, result.csv, 'text/csv;charset=utf-8');
      toast({ title: 'ML Insights CSV exported' });
    } catch (err) {
      setMlError(err instanceof Error ? err.message : String(err));
    } finally {
      setMlExporting(false);
    }
  }

  async function submitMlFeedback(input: { customerId?: string | null; insightType: string; targetId: string; targetLabel: string; feedback: string; payload?: unknown }) {
    if (!canMlFeedback) return;
    try {
      await apiFetch('/ml-insights/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      toast({ title: 'ML feedback captured', description: input.feedback.replace(/_/g, ' ') });
    } catch (err) {
      toast({ title: 'ML feedback failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    }
  }

  function open(tabId: string, targetCustomerId?: string | null) {
    if (!canDrilldown) return;
    onNavigate?.(tabId, targetCustomerId ?? undefined);
  }

  async function exportCsv() {
    if (!payload) return;
    setExporting(true);
    setError(null);
    try {
      const result = await apiFetch<{ ok: boolean; filename: string; csv: string }>('/mission-control/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ format: 'csv', payload }),
      });
      downloadText(result.filename || `kloudsecure-mission-control-${lookback}.csv`, result.csv, 'text/csv;charset=utf-8');
      toast({ title: 'Mission Control CSV exported' });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  }

  async function exportPdf() {
    if (!payload) return;
    setExporting(true);
    setError(null);
    try {
      await apiFetch('/mission-control/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ format: 'pdf', payload }),
      });
      await exportMissionPdf(payload, {
        customerLabel: scopeMode === 'all' ? 'All permitted customers' : customerName ?? selectedScopeCustomerId ?? 'Selected customer',
        filenameBase: `kloudsecure-mission-control-${payload.lookback}`,
      });
      toast({ title: 'Mission Control PDF exported' });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  }



  async function generateBriefing(kind: MissionBriefingKind, showToast = true): Promise<MissionBriefing | null> {
    if (!payload) return null;
    setBriefingLoading(kind);
    setError(null);
    try {
      const result = await apiFetch<MissionBriefing>('/mission-control/ai-briefing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, payload }),
      });
      setBriefing(result);
      if (showToast) toast({ title: result.title, description: result.usedAi ? `Generated using ${result.model}` : 'Deterministic fallback used' });
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return null;
    } finally {
      setBriefingLoading(null);
    }
  }

  async function copyBriefing() {
    if (!briefing) return;
    await copyText(briefing.markdown);
    toast({ title: 'Briefing copied' });
  }

  async function exportCurrentBriefing() {
    if (!briefing) return;
    await exportBriefingPdf(briefing, {
      customerLabel: scopeMode === 'all' ? 'All permitted customers' : customerName ?? selectedScopeCustomerId ?? 'Selected customer',
      filenameBase: `kloudsecure-${briefing.kind}`,
    });
    toast({ title: 'Briefing PDF exported' });
  }

  async function exportExecutiveSummary() {
    const result = briefing?.kind === 'executive_summary' ? briefing : await generateBriefing('executive_summary', false);
    if (!result) return;
    await exportBriefingPdf(result, {
      customerLabel: scopeMode === 'all' ? 'All permitted customers' : customerName ?? selectedScopeCustomerId ?? 'Selected customer',
      filenameBase: 'kloudsecure-executive-soc-summary',
    });
    toast({ title: 'Executive summary exported' });
  }

  const topRisk = useMemo(() => payload?.customerRisk.tiles ?? [], [payload]);
  const actions = payload?.recommendedActions ?? [];

  if (!canRead) {
    return <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900">
      Mission Control is hidden by Permissions Master. Enable Mission Control → Read for this role.
    </div>;
  }

  return <div className="slide-in space-y-5">
    <div className="relative overflow-hidden rounded-3xl border border-cyan-400/30 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6 shadow-2xl">
      <div className="absolute inset-y-0 right-0 w-2/3 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.32),transparent_42%)]" />
      <div className="relative flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.35em] text-cyan-200/90 font-black">SOC Mission Control</div>
          <h2 className="mt-1 text-4xl font-black text-white">Mission Control</h2>
          <p className="mt-2 max-w-5xl text-sm text-blue-100/80">Centralized AI SOC command view for incidents, customer risk, HuntOps, ResponseOps, connector health, evidence, SOC debt, detection quality and audited operational activity. Every card is a drilldown into the owning KloudSecure module.</p>
          {payload && <div className="mt-3 text-xs text-blue-100/70">Generated {fmtTs(payload.generatedAt)} · Scope: {scopeMode === 'all' ? 'All permitted customers' : customerName ?? selectedScopeCustomerId ?? 'Selected customer'} · {payload.scope.customerCount} customer(s)</div>}
          {payload && <div className="mt-5 grid max-w-5xl grid-cols-2 gap-3 md:grid-cols-4">
            <HeroStat label="Active incidents" value={payload.totals.activeIncidents ?? 0} helper={`${payload.totals.criticalHighIncidents ?? 0} critical/high`} status={payload.totals.criticalHighIncidents ? 'Critical' : 'Healthy'} />
            <HeroStat label="Hunt findings" value={payload.totals.huntFindings ?? 0} helper={`${payload.totals.highHuntFindings ?? 0} high-confidence`} status={payload.totals.highHuntFindings ? 'Warning' : 'Healthy'} />
            <HeroStat label="ResponseOps" value={payload.totals.playbookInventory ?? 0} helper={`${payload.totals.openRuntimeIssues ?? 0} runtime issues`} status={payload.totals.openRuntimeIssues ? 'Warning' : 'Healthy'} />
            <HeroStat label="Pipeline" value={`${payload.pipeline.length ? Math.round(payload.pipeline.reduce((acc, step) => acc + Number(step.healthPercent ?? 0), 0) / payload.pipeline.length) : 0}%`} helper="average health" status={(payload.pipeline.some((step) => step.status === 'Critical') ? 'Critical' : payload.pipeline.some((step) => step.status === 'Warning') ? 'Warning' : 'Healthy')} />
          </div>}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-xs font-semibold text-blue-100/80">Lookback</label>
            <select value={lookback} onChange={(e) => setLookback(e.target.value as Lookback)} className="mt-1 block rounded-xl border border-white/20 bg-slate-950/80 px-3 py-2 text-sm text-white">
              {LOOKBACKS.map((lb) => <option key={lb} value={lb}>{lb}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-blue-100/80">Scope</label>
            <select value={scopeMode} onChange={(e) => setScopeMode(e.target.value as 'all' | 'selected')} className="mt-1 block rounded-xl border border-white/20 bg-slate-950/80 px-3 py-2 text-sm text-white">
              <option value="all">All permitted customers</option>
              <option value="selected" disabled={!customerId}>Header-selected customer</option>
            </select>
          </div>
          {canRefresh && <button type="button" onClick={() => void load()} disabled={loading} className="rounded-xl bg-cyan-500 px-4 py-2 text-sm font-bold text-slate-950 hover:bg-cyan-400 disabled:opacity-60">{loading ? 'Loading…' : 'Refresh'}</button>}
          {canExport && payload && <button type="button" onClick={() => void exportCsv()} disabled={exporting} className="rounded-xl border border-white/25 px-4 py-2 text-sm font-bold text-white hover:bg-white/10 disabled:opacity-60">Export CSV</button>}
          {canExport && payload && <button type="button" onClick={() => void exportPdf()} disabled={exporting} className="rounded-xl border border-white/25 px-4 py-2 text-sm font-bold text-white hover:bg-white/10 disabled:opacity-60">Export PDF</button>}
          {canAiBriefing && payload && <button type="button" onClick={() => void generateBriefing('soc_briefing')} disabled={!!briefingLoading} className="rounded-xl border border-cyan-300/40 bg-cyan-400/10 px-4 py-2 text-sm font-bold text-cyan-50 hover:bg-cyan-400/20 disabled:opacity-60">{briefingLoading === 'soc_briefing' ? 'Briefing…' : 'AI SOC Briefing'}</button>}
          {canRecommendedActions && payload && <button type="button" onClick={() => void generateBriefing('recommended_actions')} disabled={!!briefingLoading} className="rounded-xl border border-cyan-300/40 bg-cyan-400/10 px-4 py-2 text-sm font-bold text-cyan-50 hover:bg-cyan-400/20 disabled:opacity-60">{briefingLoading === 'recommended_actions' ? 'Analyzing…' : 'AI Recommended Actions'}</button>}
          {canShiftHandover && payload && <button type="button" onClick={() => void generateBriefing('shift_handover')} disabled={!!briefingLoading} className="rounded-xl border border-cyan-300/40 bg-cyan-400/10 px-4 py-2 text-sm font-bold text-cyan-50 hover:bg-cyan-400/20 disabled:opacity-60">{briefingLoading === 'shift_handover' ? 'Preparing…' : 'AI Shift Handover'}</button>}
          {canExecutiveSummary && payload && <button type="button" onClick={() => void exportExecutiveSummary()} disabled={!!briefingLoading} className="rounded-xl border border-white/25 px-4 py-2 text-sm font-bold text-white hover:bg-white/10 disabled:opacity-60">{briefingLoading === 'executive_summary' ? 'Exporting…' : 'Executive Summary PDF'}</button>}
        </div>
      </div>
      {error && <div className="relative mt-4 rounded-xl border border-red-300/50 bg-red-500/20 p-3 text-sm text-red-100">{error}</div>}
    </div>

    {briefing && <BriefingPanel briefing={briefing} onOpen={(tabId) => open(tabId)} onCopy={() => void copyBriefing()} onExport={exportCurrentBriefing} />}

    {canMlRead && <Section title="ML Insights Engine" subtitle="Machine-learning insights now live in a dedicated top-level tab for incident priority, false-positive likelihood, entity baselines, connector anomalies, feedback and optional Python worker governance.">
      <div className="rounded-2xl border border-violet-500/30 bg-violet-500/10 p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="font-black text-foreground">ML has been promoted out of Mission Control</div>
            <div className="mt-1 text-sm text-muted-foreground">Open the dedicated ML Insights Engine to re-score incidents, inspect entity anomaly baselines, review model health and check the optional Python worker.</div>
          </div>
          <button type="button" onClick={() => open('ml_insights', selectedScopeCustomerId)} className="rounded-xl bg-primary px-4 py-2 text-sm font-black text-primary-foreground shadow hover:opacity-90">Open ML Insights Engine</button>
        </div>
      </div>
    </Section>}

    {payload && canAgentVisual && <AgentCoreVisual agents={payload.agents} pipeline={payload.pipeline} totals={payload.totals} onOpen={open} />}


    {payload && <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-8">
      {payload.kpis.map((kpi) => <MetricCard key={kpi.id} kpi={kpi} onOpen={() => open(kpi.targetTab, kpi.targetCustomerId)} />)}
    </div>}

    {payload && <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.05fr_1.25fr_1fr]">
      <Section title="AI SOC Agent Ecosystem" subtitle="Operational agents backed by real KloudSecure module signals.">
        <div className="space-y-3">
          {payload.agents.map((agent) => <button key={agent.id} type="button" onClick={() => open(agent.targetTab, agent.targetCustomerId)} className="w-full rounded-2xl border bg-background/50 p-4 text-left transition hover:border-primary/60 hover:bg-muted/60">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-black">{agent.label}</div>
                <div className="text-xs text-muted-foreground">{agent.module} · Last run {fmtDate(agent.lastRun)}</div>
              </div>
              <span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${statusClass(agent.status)}`}>{agent.status}</span>
            </div>
            <div className="mt-3 grid grid-cols-[80px_1fr] gap-3 text-sm">
              <div className="rounded-xl bg-muted p-2 text-center"><div className="text-xl font-black">{agent.findings}</div><div className="text-[10px] text-muted-foreground">signals</div></div>
              <div className="text-xs text-muted-foreground">{agent.recommendedAction}</div>
            </div>
          </button>)}
        </div>
      </Section>

      <Section title="Customer Risk Wall" subtitle={`Top ${topRisk.length || 0} customers from Risk Command Center cache.`}>
        {topRisk.length === 0 ? <div className="rounded-2xl border border-dashed p-5 text-sm text-muted-foreground">No cached Risk Command Center tiles yet. Open Risk Command Center and refresh it once to populate this wall.</div> : <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {topRisk.map((tile) => <button key={tile.customerId} type="button" onClick={() => open('risk_command_center', tile.customerId)} className={`rounded-2xl border p-4 text-left transition hover:-translate-y-0.5 hover:shadow-md ${riskClass(tile.riskLevel, tile.riskIndex)}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-black">{tile.customerName}</div>
                <div className="text-xs text-muted-foreground">{tile.connectorHealth} connector · {tile.edrHealth} EDR</div>
              </div>
              <div className="text-right"><div className="text-2xl font-black">{tile.riskIndex}</div><div className="text-xs font-bold">{tile.riskLevel}</div></div>
            </div>
            <p className="mt-3 line-clamp-2 text-xs text-muted-foreground">{tile.reason}</p>
            <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
              <div className="rounded-xl bg-background/70 p-2"><div className="font-black">{tile.currentIncidentCount}</div><div className="text-muted-foreground">Open</div></div>
              <div className="rounded-xl bg-background/70 p-2"><div className="font-black">{tile.criticalUnresolvedCount}</div><div className="text-muted-foreground">Crit</div></div>
              <div className="rounded-xl bg-background/70 p-2"><div className="font-black">{tile.tiHitCount}</div><div className="text-muted-foreground">TI</div></div>
              <div className="rounded-xl bg-background/70 p-2"><div className="font-black">{tile.slaBreachRisk}</div><div className="text-muted-foreground">SLA</div></div>
            </div>
          </button>)}
        </div>}
      </Section>

      <Section title="Focus Today" subtitle="Prioritized actions generated from current module signals.">
        {actions.length === 0 ? <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-sm text-emerald-700 dark:text-emerald-200">No urgent Mission Control actions from cached signals.</div> : <div className="space-y-3">
          {actions.map((action, idx) => <button key={`${action.title}-${idx}`} type="button" onClick={() => open(action.targetTab)} className="w-full rounded-2xl border bg-background/60 p-4 text-left transition hover:border-primary/60 hover:bg-muted/60">
            <div className="font-black">{idx + 1}. {action.title}</div>
            <div className="mt-1 text-xs text-muted-foreground">{action.detail}</div>
          </button>)}
        </div>}
      </Section>
    </div>}

    {payload && <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
      <Section title="Incident Response" subtitle="High-priority incident and customer update activity.">
        <div className="grid grid-cols-3 gap-3">
          <button type="button" onClick={() => open('incidents')} className="rounded-2xl border bg-background/60 p-4 text-left hover:bg-muted/60"><div className="text-2xl font-black">{payload.incidentResponse.activeIncidents}</div><div className="text-xs text-muted-foreground">Active incidents</div></button>
          <button type="button" onClick={() => open('incidents')} className="rounded-2xl border bg-background/60 p-4 text-left hover:bg-muted/60"><div className="text-2xl font-black">{payload.incidentResponse.criticalHighIncidents}</div><div className="text-xs text-muted-foreground">Critical/high</div></button>
          <button type="button" onClick={() => open('log_master')} className="rounded-2xl border bg-background/60 p-4 text-left hover:bg-muted/60"><div className="text-2xl font-black">{payload.incidentResponse.customerUpdateEvents}</div><div className="text-xs text-muted-foreground">Customer updates</div></button>
        </div>
        <div className="mt-4 space-y-2">
          {payload.incidentResponse.topCustomers.slice(0, 4).map((tile) => <button key={tile.customerId} type="button" onClick={() => open('incidents', tile.customerId)} className="flex w-full items-center justify-between rounded-xl border bg-background/50 p-3 text-left text-sm hover:bg-muted/50"><span className="font-bold">{tile.customerName}</span><span className="text-muted-foreground">{tile.criticalUnresolvedCount} critical/high · risk {tile.riskIndex}</span></button>)}
        </div>
      </Section>

      <Section title="Hunt & Threat Intel" subtitle="HuntOps findings, run health and bookmarks.">
        <div className="grid grid-cols-4 gap-3">
          <button type="button" onClick={() => open('hunts')} className="rounded-2xl border bg-background/60 p-4 text-left hover:bg-muted/60"><div className="text-2xl font-black">{payload.huntThreatIntel.huntFindings}</div><div className="text-xs text-muted-foreground">Findings</div></button>
          <button type="button" onClick={() => open('hunts')} className="rounded-2xl border bg-background/60 p-4 text-left hover:bg-muted/60"><div className="text-2xl font-black">{payload.huntThreatIntel.highHuntFindings}</div><div className="text-xs text-muted-foreground">High confidence</div></button>
          <button type="button" onClick={() => open('hunts')} className="rounded-2xl border bg-background/60 p-4 text-left hover:bg-muted/60"><div className="text-2xl font-black">{payload.huntThreatIntel.failedHuntRuns}</div><div className="text-xs text-muted-foreground">Failed runs</div></button>
          <button type="button" onClick={() => open('hunts')} className="rounded-2xl border bg-background/60 p-4 text-left hover:bg-muted/60"><div className="text-2xl font-black">{payload.huntThreatIntel.openBookmarks}</div><div className="text-xs text-muted-foreground">Bookmarks</div></button>
        </div>
        <div className="mt-4 space-y-2">
          {payload.huntThreatIntel.recentFindings.slice(0, 4).map((row, idx) => <button key={idx} type="button" onClick={() => open('hunts', String(row.customer_id ?? ''))} className="w-full rounded-xl border bg-background/50 p-3 text-left text-sm hover:bg-muted/50"><span className="font-bold">{text(row.title)}</span><span className="ml-2 text-xs text-muted-foreground">{text(row.customerName)} · {text(row.severity)} · {text(row.confidence)}%</span></button>)}
        </div>
      </Section>
    </div>}

    {payload && <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
      <Section title="ResponseOps" subtitle="Playbook inventory, access policies, deployments and runtime checks.">
        <div className="grid grid-cols-5 gap-3">
          <button type="button" onClick={() => open('responseops')} className="rounded-2xl border bg-background/60 p-4 text-left hover:bg-muted/60"><div className="text-2xl font-black">{payload.responseOps.playbookInventory}</div><div className="text-xs text-muted-foreground">Discovered</div></button>
          <button type="button" onClick={() => open('masters')} className="rounded-2xl border bg-background/60 p-4 text-left hover:bg-muted/60"><div className="text-2xl font-black">{payload.responseOps.playbookPolicies}</div><div className="text-xs text-muted-foreground">Allowed</div></button>
          <button type="button" onClick={() => open('responseops')} className="rounded-2xl border bg-background/60 p-4 text-left hover:bg-muted/60"><div className="text-2xl font-black">{payload.responseOps.responseDeployments}</div><div className="text-xs text-muted-foreground">Deployments</div></button>
          <button type="button" onClick={() => open('responseops')} className="rounded-2xl border bg-background/60 p-4 text-left hover:bg-muted/60"><div className="text-2xl font-black">{payload.responseOps.openRuntimeIssues}</div><div className="text-xs text-muted-foreground">Runtime issues</div></button>
          <button type="button" onClick={() => open('responseops')} className="rounded-2xl border bg-background/60 p-4 text-left hover:bg-muted/60"><div className="text-2xl font-black">{payload.responseOps.criticalRuntimeIssues}</div><div className="text-xs text-muted-foreground">High/Critical</div></button>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          {payload.responseOps.recentIssues.slice(0, 4).map((row, idx) => <button key={idx} type="button" onClick={() => open('responseops', String(row.customer_id ?? ''))} className="rounded-xl border bg-background/50 p-3 text-left text-sm hover:bg-muted/50"><div className="font-bold">{text(row.title)}</div><div className="text-xs text-muted-foreground">{text(row.customerName)} · {text(row.severity)} · {text(row.affected_object)}</div></button>)}
        </div>
      </Section>

      <Section title="Data Health + Governance" subtitle="Connector cache, SOC debt, detection quality and audit/log activity.">
        <div className="grid grid-cols-3 gap-3">
          <button type="button" onClick={() => open('connector_health')} className="rounded-2xl border bg-background/60 p-4 text-left hover:bg-muted/60"><div className="text-2xl font-black">{payload.dataHealth.connectorCritical}</div><div className="text-xs text-muted-foreground">Critical connectors</div></button>
          <button type="button" onClick={() => open('connector_health')} className="rounded-2xl border bg-background/60 p-4 text-left hover:bg-muted/60"><div className="text-2xl font-black">{payload.dataHealth.connectorWarning}</div><div className="text-xs text-muted-foreground">Warning connectors</div></button>
          <button type="button" onClick={() => open('log_master')} className="rounded-2xl border bg-background/60 p-4 text-left hover:bg-muted/60"><div className="text-2xl font-black">{payload.dataHealth.recentAuditEvents}</div><div className="text-xs text-muted-foreground">Audit events</div></button>
          <button type="button" onClick={() => open('soc_debt')} className="rounded-2xl border bg-background/60 p-4 text-left hover:bg-muted/60"><div className="text-2xl font-black">{payload.dataHealth.socDebtSnapshots}</div><div className="text-xs text-muted-foreground">SOC Debt cache</div></button>
          <button type="button" onClick={() => open('detection_quality')} className="rounded-2xl border bg-background/60 p-4 text-left hover:bg-muted/60"><div className="text-2xl font-black">{payload.dataHealth.detectionQualitySnapshots}</div><div className="text-xs text-muted-foreground">Detection Quality cache</div></button>
          <button type="button" onClick={() => open('connector_health')} className="rounded-2xl border bg-background/60 p-4 text-left hover:bg-muted/60"><div className="text-2xl font-black">{payload.dataHealth.connectorSnapshots}</div><div className="text-xs text-muted-foreground">Health snapshots</div></button>
        </div>
      </Section>
    </div>}

    {payload && canWorkflowPipeline && <WorkflowPipelinePanel pipeline={payload.pipeline} onOpen={(tabId) => open(tabId)} />}

    {payload?.diagnostics?.length ? <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-700 dark:text-amber-200">
      <div className="font-black">Mission Control diagnostics</div>
      <div className="mt-1 text-xs opacity-80">Some optional module tables/cache rows were unavailable. The dashboard still loaded with available signals.</div>
      <details className="mt-2"><summary className="cursor-pointer text-xs font-bold">Show diagnostics</summary><pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap text-xs">{payload.diagnostics.join('\n')}</pre></details>
    </div> : null}
  </div>;
}
